import { Stripe } from "@stripe/stripe-js";
import { getDonationPlanCode } from "Utils/Components/Plan";
import { DonationType } from "Utils/Constants/Donate";
import { DonationFormState } from "./DonationForm";
import { calcCoverFee } from "./checkoutV2.constants";

/** Dollars → cents (the checkout API works in cents; the form holds dollars). */
export const toCents = (dollars: number) => Math.round(dollars * 100);

export const frequencyToDonationType = (
  frequency: DonationFormState["frequency"]
) => (frequency === "monthly" ? DonationType.monthly : DonationType.oneTime);

/**
 * Confirms a Stripe PaymentIntent by client secret (mirrors the legacy
 * retrieve → confirm step). Returns the confirmed paymentIntent to send to
 * /checkout/v2.
 */
export async function confirmPaymentIntent(stripe: Stripe, secret: string) {
  const retrieved = await stripe.retrievePaymentIntent(secret);
  const status = retrieved?.paymentIntent?.status;
  if (status === "requires_confirmation" || status === "requires_action") {
    const result = await stripe.confirmCardPayment(secret);
    if (result.error) throw new Error(result.error.message);
    return result.paymentIntent;
  }
  return retrieved.paymentIntent;
}

/** The extra (cover-fee) amount in cents for a donation, or 0. */
export const additionalCents = (state: DonationFormState, effectiveAmount: number) =>
  state.coverFees ? toCents(calcCoverFee(effectiveAmount)) : 0;

/**
 * Builds the /checkout/v2 request body for a card donation. Amounts in the form
 * state are dollars; the API wants cents. Pass `password` only when creating a
 * new account.
 */
export function buildCheckoutPayload(args: {
  state: DonationFormState;
  effectiveAmount: number; // dollars
  paymentIntent: unknown;
  firstName: string;
  lastName: string;
  password?: string;
}) {
  const { state, effectiveAmount, paymentIntent, firstName, lastName, password } = args;
  const donationAmount = toCents(effectiveAmount);
  const donationAdditionalAmount = additionalCents(state, effectiveAmount);
  const donationType = frequencyToDonationType(state.frequency);
  return {
    customer: {
      first_name: firstName,
      last_name: lastName,
      email: state.email.trim(),
      auth0_id: undefined,
      meta_data: { language: "en" },
    },
    paymentMethod: { type: "card", paymentIntent },
    donationAmount,
    donationAdditionalAmount,
    amount: donationAmount + donationAdditionalAmount,
    monthly: state.frequency === "monthly",
    plan: getDonationPlanCode(donationAmount, donationType),
    autoRenew: state.autoRenew,
    isGift: false,
    gift: undefined as string | undefined,
    coupon: undefined as string | undefined,
    dev: process.env.REACT_APP_NODE_ENV === "dev",
    password: password || undefined,
  };
}
